// src/app/components/prompt-tracking/prompt-intelligence-header.tsx
// Extracted page header — shows title, description, and live summary stats.

import {
  Sparkles,
  Clock,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import type { PromptSummaryStats } from '../../types/prompt-tracking-types';

interface PromptIntelligenceHeaderProps {
  stats: PromptSummaryStats;
}

export function PromptIntelligenceHeader({ stats }: PromptIntelligenceHeaderProps) {
  return (
    <div
      className="px-4 md:px-8 py-6 md:py-8 border-b"
      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
    >
      {/* Page breadcrumb */}
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <Sparkles
          className="w-5 h-5 flex-shrink-0"
          style={{ color: 'var(--muted-foreground)' }}
        />
        <span
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          Prompt Intelligence
        </span>
      </div>

      {/* Headline */}
      <h2
        style={{
          fontSize: 'clamp(var(--text-xl), 4.5vw, var(--text-2xl))',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--foreground)',
          lineHeight: '1.3',
          marginBottom: '12px',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        Track conversations. Optimize positioning.
      </h2>

      {/* Description */}
      <p
        style={{
          color: 'var(--muted-foreground)',
          fontSize: 'var(--text-sm)',
          lineHeight: '1.5',
          marginBottom: '20px',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        Monitor how AI models respond to specific prompts across different
        conversation contexts. Understand sentiment patterns and optimize
        your positioning based on real AI behavior.
      </p>

      {/* Summary stats row — outer flex-wrap for label vs stats group,
          inner flex-wrap with gap-x/gap-y so stat chunks wrap onto
          multiple lines on narrow widths instead of clipping. */}
      <div className="flex items-center gap-x-3 gap-y-2 flex-wrap">
        <span
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          Prompt Status:
        </span>

        <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
          {/* Total prompts */}
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: 'var(--alpha-teal)' }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--foreground)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {stats.totalPrompts}
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              active prompts
            </span>
          </div>

          {/* Avg recommendation rate */}
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--signal-blue)' }} />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--foreground)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {stats.avgRecommendationRate}%
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              avg recommendation rate
            </span>
          </div>

          {/* Needs attention */}
          <div className="flex items-center gap-2">
            <AlertTriangle
              className="w-3.5 h-3.5 flex-shrink-0"
              style={{ color: 'var(--infrared-coral)' }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--foreground)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {stats.needsAttention}
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              need attention
            </span>
          </div>

          {/* Trending up */}
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--chart-3)' }} />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--foreground)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {stats.upTrending}
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              trending up
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}